import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from 'axios';

interface Reading {
  id: number;
  meter_id: number;
  meter_name: string;
  meter_code: string;
  meter_type: string;
  unit: string;
  reading_date: string;
  reading_time: string;
  reading_value: number;
  previous_value: number | null;
  consumption: number | null;
  notes: string | null;
  entry_method: string;
  created_by_name: string | null;
  created_at: string;
  production_value: number | null;
  production_unit: string | null;
  production_type: string | null;
}

interface NumberRange {
  min: string;
  max: string;
}

const MeterReadings: React.FC = () => {
  const [readings, setReadings] = useState<Reading[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedRow, setSelectedRow] = useState<Reading | null>(null);

  const [meterCodeFilter, setMeterCodeFilter] = useState('');
  const [meterNameFilter, setMeterNameFilter] = useState('');
  const [meterTypeFilter, setMeterTypeFilter] = useState('');
  const [entryMethodFilter, setEntryMethodFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [notesFilter, setNotesFilter] = useState('');
  const [productionUnitFilter, setProductionUnitFilter] = useState('');
  const [productionTypeFilter, setProductionTypeFilter] = useState('');

  const [readingDateFrom, setReadingDateFrom] = useState('');
  const [readingDateTo, setReadingDateTo] = useState('');
  const [createdDateFrom, setCreatedDateFrom] = useState('');
  const [createdDateTo, setCreatedDateTo] = useState('');

  const [readingValueRange, setReadingValueRange] = useState<NumberRange>({ min: '', max: '' });
  const [previousValueRange, setPreviousValueRange] = useState<NumberRange>({ min: '', max: '' });
  const [consumptionRange, setConsumptionRange] = useState<NumberRange>({ min: '', max: '' });
  const [productionValueRange, setProductionValueRange] = useState<NumberRange>({ min: '', max: '' });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchReadings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/energy-management/readings?limit=5000', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadings(response.data || []);
      setPage(0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load meter readings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReadings();
  }, []);

  const entryMethods = useMemo(() => Array.from(new Set(readings.map(r => r.entry_method).filter(Boolean))), [readings]);
  const meterTypes = useMemo(() => Array.from(new Set(readings.map(r => r.meter_type).filter(Boolean))), [readings]);
  const productionUnits = useMemo(() => Array.from(new Set(readings.map(r => r.production_unit).filter(Boolean))) as string[], [readings]);
  const productionTypes = useMemo(() => Array.from(new Set(readings.map(r => r.production_type).filter(Boolean))) as string[], [readings]);

  const includesText = (value: string | null | undefined, search: string) => {
    if (!search) return true;
    return String(value || '').toLowerCase().includes(search.toLowerCase());
  };

  const inRange = (value: number | null | undefined, range: NumberRange) => {
    if (range.min === '' && range.max === '') return true;
    if (value === null || value === undefined) return false;
    const minOk = range.min === '' || value >= Number(range.min);
    const maxOk = range.max === '' || value <= Number(range.max);
    return minOk && maxOk;
  };

  const inDateRange = (value: string | null | undefined, from: string, to: string) => {
    if (!from && !to) return true;
    if (!value) return false;
    const dateOnly = String(value).slice(0, 10);
    const fromOk = !from || dateOnly >= from;
    const toOk = !to || dateOnly <= to;
    return fromOk && toOk;
  };

  const filteredReadings = useMemo(() => {
    const list = readings.filter(r => {
      if (!includesText(r.meter_code, meterCodeFilter)) return false;
      if (!includesText(r.meter_name, meterNameFilter)) return false;
      if (meterTypeFilter && r.meter_type !== meterTypeFilter) return false;
      if (entryMethodFilter && r.entry_method !== entryMethodFilter) return false;
      if (!includesText(r.created_by_name, createdByFilter)) return false;
      if (!includesText(r.notes, notesFilter)) return false;
      if (productionUnitFilter && r.production_unit !== productionUnitFilter) return false;
      if (productionTypeFilter && r.production_type !== productionTypeFilter) return false;

      if (!inDateRange(r.reading_date, readingDateFrom, readingDateTo)) return false;
      if (!inDateRange(r.created_at, createdDateFrom, createdDateTo)) return false;

      if (!inRange(r.reading_value, readingValueRange)) return false;
      if (!inRange(r.previous_value, previousValueRange)) return false;
      if (!inRange(r.consumption, consumptionRange)) return false;
      if (!inRange(r.production_value, productionValueRange)) return false;

      return true;
    });

    return list;
  }, [
    readings,
    meterCodeFilter,
    meterNameFilter,
    meterTypeFilter,
    entryMethodFilter,
    createdByFilter,
    notesFilter,
    productionUnitFilter,
    productionTypeFilter,
    readingDateFrom,
    readingDateTo,
    createdDateFrom,
    createdDateTo,
    readingValueRange,
    previousValueRange,
    consumptionRange,
    productionValueRange,
  ]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredReadings.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredReadings.length, page, rowsPerPage]);

  const clearFilters = () => {
    setMeterCodeFilter('');
    setMeterNameFilter('');
    setMeterTypeFilter('');
    setEntryMethodFilter('');
    setCreatedByFilter('');
    setNotesFilter('');
    setProductionUnitFilter('');
    setProductionTypeFilter('');
    setReadingDateFrom('');
    setReadingDateTo('');
    setCreatedDateFrom('');
    setCreatedDateTo('');
    setReadingValueRange({ min: '', max: '' });
    setPreviousValueRange({ min: '', max: '' });
    setConsumptionRange({ min: '', max: '' });
    setProductionValueRange({ min: '', max: '' });
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700, color: '#00695C' }}>
        Meter Readings
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" onClick={clearFilters}>Clear Filters</Button>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReadings} disabled={loading}>
              Refresh
            </Button>
          </Box>
        </Box>

        <Grid container spacing={1.5}>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" label="Meter Code" value={meterCodeFilter} onChange={(e) => setMeterCodeFilter(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Meter Name" value={meterNameFilter} onChange={(e) => setMeterNameFilter(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" select label="Meter Type" value={meterTypeFilter} onChange={(e) => setMeterTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {meterTypes.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" select label="Entry Method" value={entryMethodFilter} onChange={(e) => setEntryMethodFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {entryMethods.map(method => (<MenuItem key={method} value={method}>{method}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Created By" value={createdByFilter} onChange={(e) => setCreatedByFilter(e.target.value)} />
          </Grid>

          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Read Date From" value={readingDateFrom} onChange={(e) => setReadingDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Read Date To" value={readingDateTo} onChange={(e) => setReadingDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Created From" value={createdDateFrom} onChange={(e) => setCreatedDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" type="date" label="Created To" value={createdDateTo} onChange={(e) => setCreatedDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" select label="Production Unit" value={productionUnitFilter} onChange={(e) => setProductionUnitFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {productionUnits.map(unit => (<MenuItem key={unit} value={unit}>{unit}</MenuItem>))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField fullWidth size="small" select label="Production Type" value={productionTypeFilter} onChange={(e) => setProductionTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {productionTypes.map(type => (<MenuItem key={type} value={type}>{type}</MenuItem>))}
            </TextField>
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Read Min" value={readingValueRange.min} onChange={(e) => setReadingValueRange({ ...readingValueRange, min: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Read Max" value={readingValueRange.max} onChange={(e) => setReadingValueRange({ ...readingValueRange, max: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Prev Min" value={previousValueRange.min} onChange={(e) => setPreviousValueRange({ ...previousValueRange, min: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Prev Max" value={previousValueRange.max} onChange={(e) => setPreviousValueRange({ ...previousValueRange, max: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Cons Min" value={consumptionRange.min} onChange={(e) => setConsumptionRange({ ...consumptionRange, min: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Cons Max" value={consumptionRange.max} onChange={(e) => setConsumptionRange({ ...consumptionRange, max: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Prod Min" value={productionValueRange.min} onChange={(e) => setProductionValueRange({ ...productionValueRange, min: e.target.value })} />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField fullWidth size="small" type="number" label="Prod Max" value={productionValueRange.max} onChange={(e) => setProductionValueRange({ ...productionValueRange, max: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField fullWidth size="small" label="Notes Contains" value={notesFilter} onChange={(e) => setNotesFilter(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>All Meter Readings</Typography>
          <Chip color="info" label={`Showing ${filteredReadings.length} / ${readings.length}`} />
        </Box>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Meter</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Read Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="right">Reading</TableCell>
                <TableCell align="right">Previous</TableCell>
                <TableCell align="right">Consumption</TableCell>
                <TableCell align="right">Production</TableCell>
                <TableCell>Entry</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReadings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((reading) => (
                <TableRow
                  key={reading.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setSelectedRow(reading)}
                >
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{reading.meter_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{reading.meter_code}</Typography>
                  </TableCell>
                  <TableCell><Chip label={reading.meter_type} size="small" /></TableCell>
                  <TableCell>{String(reading.reading_date).slice(0, 10)}</TableCell>
                  <TableCell>{reading.reading_time}</TableCell>
                  <TableCell align="right"><strong>{reading.reading_value}</strong> {reading.unit}</TableCell>
                  <TableCell align="right">{reading.previous_value !== null ? `${reading.previous_value} ${reading.unit}` : '-'}</TableCell>
                  <TableCell align="right">
                    {reading.consumption !== null ? (
                      <Chip
                        label={`${reading.consumption} ${reading.unit}`}
                        size="small"
                        color={reading.consumption > 0 ? 'success' : 'default'}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {reading.production_value !== null
                      ? `${reading.production_value} ${reading.production_unit || ''}`
                      : '-'}
                  </TableCell>
                  <TableCell><Chip label={reading.entry_method} size="small" variant="outlined" /></TableCell>
                  <TableCell>{String(reading.created_at).replace('T', ' ').slice(0, 19)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredReadings.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Dialog open={!!selectedRow} onClose={() => setSelectedRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>Reading Details</DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}><strong>ID:</strong> {selectedRow.id}</Grid>
              <Grid item xs={12} md={6}><strong>Meter ID:</strong> {selectedRow.meter_id}</Grid>
              <Grid item xs={12} md={6}><strong>Meter Name:</strong> {selectedRow.meter_name}</Grid>
              <Grid item xs={12} md={6}><strong>Meter Code:</strong> {selectedRow.meter_code}</Grid>
              <Grid item xs={12} md={6}><strong>Meter Type:</strong> {selectedRow.meter_type}</Grid>
              <Grid item xs={12} md={6}><strong>Unit:</strong> {selectedRow.unit}</Grid>
              <Grid item xs={12} md={6}><strong>Reading Date:</strong> {String(selectedRow.reading_date).slice(0, 10)}</Grid>
              <Grid item xs={12} md={6}><strong>Reading Time:</strong> {selectedRow.reading_time}</Grid>
              <Grid item xs={12} md={6}><strong>Reading Value:</strong> {selectedRow.reading_value}</Grid>
              <Grid item xs={12} md={6}><strong>Previous Value:</strong> {selectedRow.previous_value ?? '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Consumption:</strong> {selectedRow.consumption ?? '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Production Value:</strong> {selectedRow.production_value ?? '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Production Unit:</strong> {selectedRow.production_unit || '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Production Type:</strong> {selectedRow.production_type || '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Entry Method:</strong> {selectedRow.entry_method}</Grid>
              <Grid item xs={12} md={6}><strong>Created By:</strong> {selectedRow.created_by_name || '-'}</Grid>
              <Grid item xs={12} md={6}><strong>Created At:</strong> {String(selectedRow.created_at).replace('T', ' ').slice(0, 19)}</Grid>
              <Grid item xs={12}><strong>Notes:</strong> {selectedRow.notes || '-'}</Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MeterReadings;
